import React, { useMemo, useState, useEffect } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Form,
  ListGroup,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../../utils/constants";
import { userService } from "../../../services/userService";
import AdminUserChat from "../../Chat/AdminUserChat";

const TabsName = {
  info: "Инфо о себе",
  feed: "Своя лента",
  Feed: "Лента",
  Search: "Поиск",
  Main: "Совпадения",
  Chats: "Чаты",
};

const UserProfileModal = ({ show, onHide, user, onBlockToggle, onDelete }) => {
  const [note, setNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [showFullInfromtaion, setShowFullInfromtaion] = useState(false);
  const [activePhotoIdx, setActivePhotoIdx] = useState(0);
  const [profileUser, setProfileUser] = useState(user || null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showChatsModal, setShowChatsModal] = useState(false);
  const [userChats, setUserChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [selectedChatMessages, setSelectedChatMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const lastLoginText = useMemo(() => {
    if (!profileUser?.lastLoginAt) return "—";
    const d = new Date(profileUser.lastLoginAt);
    // eslint-disable-next-line no-restricted-globals
    return isNaN(d.getTime())
      ? String(profileUser.lastLoginAt)
      : d.toLocaleString("ru-RU");
  }, [profileUser?.lastLoginAt]);

  useEffect(() => {
    setProfileUser(user || null);
  }, [user]);

  useEffect(() => {
    if (!show || !user?._id) return;
    setNote("");
    setNoteLoading(true);
    userService
      .getUserLogs(user._id)
      .then((logs) => {
        const noteLogs = Array.isArray(logs)
          ? logs.filter((l) => l.action === "note")
          : [];
        const latest = noteLogs.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        )[0];
        setNote(latest?.message || "");
      })
      .catch(() => setNote(""))
      .finally(() => setNoteLoading(false));
  }, [show, user?._id]);

  useEffect(() => {
    if (!show || !user?._id) return;
    setHistory([]);
    setHistoryLoading(true);
    userService
      .getHistoryActions(user._id)
      .then((items) => setHistory(Array.isArray(items) ? items : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [show, user?._id]);

  if (!profileUser) return null;

  const photos = profileUser.profileImage || [];
  const mainPhoto = photos[activePhotoIdx] || null;

  const handleCHnageShowFullInfo = () => {
    setShowFullInfromtaion(!showFullInfromtaion);
  };

  const handleBlockToggleClick = () => {
    const block = profileUser.status !== "blocked";
    if (onBlockToggle) {
      onBlockToggle(profileUser, block);
      return;
    }
    (block
      ? userService.blockUser({ userId: profileUser._id })
      : userService.unblockUser({ userId: profileUser._id })
    )
      .then(() => {
        setProfileUser((prev) => ({
          ...prev,
          status: block ? "blocked" : "done",
        }));
        toast.success(
          block ? "Пользователь заблокирован" : "Пользователь разблокирован",
        );
      })
      .catch((e) => {
        toast.error(
          e?.response?.data?.message || "Ошибка при изменении статуса",
        );
      });
  };

  const handleDeleteClick = () => {
    if (profileUser.status === "deleted") {
      // Восстановление аккаунта
      if (!window.confirm("Восстановить аккаунт пользователя?")) return;
      userService
        .restoreUser(profileUser._id)
        .then(() => {
          toast.success("Аккаунт восстановлен");
          setProfileUser((prev) => ({ ...prev, status: "done" }));
        })
        .catch((e) => {
          toast.error(
            e?.response?.data?.message || "Ошибка восстановления пользователя",
          );
        });
    } else {
      if (onDelete) {
        onDelete(profileUser);
        return;
      }
      // Удаление аккаунта
      if (!window.confirm("Удалить аккаунт пользователя?")) return;
      userService
        .deleteUser(profileUser._id)
        .then(() => {
          toast.success("Пользователь удалён");
          onHide?.();
        })
        .catch((e) => {
          toast.error(
            e?.response?.data?.message || "Ошибка удаления пользователя",
          );
        });
    }
  };

  const handleSaveNote = async () => {
    if (!user?._id) return;
    setNoteLoading(true);
    try {
      await userService.saveUserNote(user._id, note);
      toast.success("Заметка сохранена");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Ошибка сохранения заметки");
    } finally {
      setNoteLoading(false);
    }
  };

  const openLinkedProfileByMongoId = async (mongoId) => {
    if (!mongoId) return;
    try {
      const u = await userService.getUserByIdAdmin(mongoId);
      setProfileUser(u || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Не удалось открыть профиль");
    }
  };

  const openLinkedProfileByChatId = async (chatId) => {
    if (!chatId) return;
    try {
      const u = await userService.getUserByChatIdAdmin(chatId);
      setProfileUser(u || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Не удалось открыть профиль");
    }
  };

  const openAllChats = async () => {
    if (!profileUser?._id) return;
    setShowChatsModal(true);
    setSelectedChatId(null);
    setSelectedChatMessages([]);
    setChatsLoading(true);
    try {
      const chats = await userService.getUserChats(profileUser._id);
      setUserChats(Array.isArray(chats) ? chats : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Не удалось загрузить чаты");
      setUserChats([]);
    } finally {
      setChatsLoading(false);
    }
  };

  const openChatMessages = async (chat) => {
    const cid = chat?.id || chat?.chat_id;
    if (!cid) return;
    setSelectedChatId(cid);
    setMessagesLoading(true);
    setSelectedChatMessages([]);
    try {
      const msgs = await userService.getChatMessagesAdmin(cid);
      setSelectedChatMessages(Array.isArray(msgs) ? msgs : []);
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Не удалось загрузить сообщения",
      );
    } finally {
      setMessagesLoading(false);
    }
  };

  const renderHistoryItem = (item) => {
    const ts = item?.createdAt
      ? new Date(item.createdAt).toLocaleString("ru-RU")
      : "";
    const type = item?.type || "";
    const meta = item?.meta || {};

    if (type === "nav_tab") {
      return (
        <span>
          {ts} — Открыл вкладку: <b>{TabsName[meta.tab]}</b>
        </span>
      );
    }
    if (type === "chat_open") {
      return (
        <span>
          {ts} — Открыл чат с{" "}
          <Button
            variant="link"
            size="sm"
            className="p-0 align-baseline"
            onClick={() => openLinkedProfileByChatId(meta.userChatId)}
          >
            @{meta.username || meta.name || meta.userChatId}
          </Button>{" "}
          (id: {meta.userChatId})
        </span>
      );
    }
    if (type === "search_query" || type === "search_submit") {
      return (
        <span>
          {ts} — Поиск: "<b>{meta.query || ""}</b>"
        </span>
      );
    }
    if (type === "call_offer") {
      return (
        <span>
          {ts} — <b>Звонок (исходящий)</b>{" "}
          {meta.offer?.username ? (
            <Button
              variant="link"
              size="sm"
              className="p-0 align-baseline"
              onClick={() => openLinkedProfileByMongoId(meta.toUserId)}
            >
              @{meta.offer.username}
            </Button>
          ) : (
            meta.toUserId
          )}
        </span>
      );
    }
    if (type === "call_answer") {
      return (
        <span>
          {ts} — <b>Звонок: принят/ответ</b>
        </span>
      );
    }
    if (type === "call_end") {
      return (
        <span>
          {ts} — <b>Звонок завершён</b>
        </span>
      );
    }
    if (type === "nav_notifications") {
      return (
        <span>
          {ts} — Открыл <b>Уведомления</b>
        </span>
      );
    }
    if (type === "nav_main_profile") {
      return (
        <span>
          {ts} — Открыл <b>Профиль</b>
        </span>
      );
    }
    if (type === "nav_profile_info") {
      return (
        <span>
          {ts} — Открыл <b>Информацию о себе</b>
        </span>
      );
    }
    if (type === "nav_verifycation") {
      return (
        <span>
          {ts} — Открыл <b>Верификацию</b>
        </span>
      );
    }
    if (type === "verify_submit") {
      return (
        <span>
          {ts} — Отправил <b>Запрос верификации</b>
        </span>
      );
    }
    if (type === "nav_settings") {
      return (
        <span>
          {ts} — Открыл <b>Настройки</b>
        </span>
      );
    }
    if (type === "settings_open_section") {
      return (
        <span>
          {ts} — Настройки: открыл раздел <b>{meta.section}</b>
        </span>
      );
    }
    if (type === "settings_logout") {
      return (
        <span>
          {ts} — Настройки: <b>Выход</b>
        </span>
      );
    }
    if (type === "settings_delete") {
      return (
        <span>
          {ts} — Настройки: <b>Удаление аккаунта</b>
        </span>
      );
    }
    if (type === "settings_share") {
      return (
        <span>
          {ts} — Настройки: <b>Поделиться</b>
        </span>
      );
    }
    if (type === "profile_tab") {
      return (
        <span>
          {ts} — Профиль: вкладка - <b>{TabsName[meta.tab]}</b>
        </span>
      );
    }

    return (
      <span>
        {ts} — <b>{type}</b>
      </span>
    );
  };

  const handleDownloadHistory = () => {
    if (!history || history.length === 0) {
      toast.warning("Нет истории для скачивания");
      return;
    }

    // Формируем текст истории
    const historyText = history
      .map((item) => {
        const ts = item?.createdAt
          ? new Date(item.createdAt).toLocaleString("ru-RU")
          : "";
        const type = item?.type || "";
        const meta = item?.meta || {};

        let line = `${ts} — ${type}`;

        if (type === "nav_tab") {
          line = `${ts} — Открыл вкладку: ${TabsName[meta.tab] || meta.tab}`;
        } else if (type === "chat_open") {
          line = `${ts} — Открыл чат с ${meta.username || meta.name || meta.userChatId} (id: ${meta.userChatId})`;
        } else if (type === "search_query" || type === "search_submit") {
          line = `${ts} — Поиск: "${meta.query || ""}"`;
        } else if (type === "call_offer") {
          line = `${ts} — Звонок (исходящий) ${meta.offer?.username ? `@${meta.offer.username}` : meta.toUserId}`;
        } else if (type === "call_answer") {
          line = `${ts} — Звонок: принят/ответ`;
        } else if (type === "call_end") {
          line = `${ts} — Звонок завершён`;
        } else if (type === "nav_notifications") {
          line = `${ts} — Открыл Уведомления`;
        } else if (type === "nav_main_profile") {
          line = `${ts} — Открыл Профиль`;
        } else if (type === "nav_profile_info") {
          line = `${ts} — Открыл Информацию о себе`;
        } else if (type === "nav_verifycation") {
          line = `${ts} — Открыл Верификацию`;
        } else if (type === "verify_submit") {
          line = `${ts} — Отправил Запрос верификации`;
        } else if (type === "nav_settings") {
          line = `${ts} — Открыл Настройки`;
        } else if (type === "settings_open_section") {
          line = `${ts} — Настройки: открыл раздел ${meta.section}`;
        } else if (type === "settings_logout") {
          line = `${ts} — Настройки: Выход`;
        } else if (type === "settings_delete") {
          line = `${ts} — Настройки: Удаление аккаунта`;
        } else if (type === "settings_share") {
          line = `${ts} — Настройки: Поделиться`;
        } else if (type === "profile_tab") {
          line = `${ts} — Профиль: вкладка - ${TabsName[meta.tab] || meta.tab}`;
        }

        return line;
      })
      .join("\n");

    // Создаем Blob и ссылку для скачивания
    const blob = new Blob([historyText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `history_${profileUser.login || profileUser._id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("История действий сохранена");
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Профиль пользователя</Modal.Title>
        <Button
          variant="outline-dark"
          size="sm"
          onClick={handleCHnageShowFullInfo}
          style={{ border: "none" }}
        >
          𝓲
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={4} className="mb-3">
            {mainPhoto?.name ? (
              <>
                <img
                  src={API_BASE_URL + "/" + mainPhoto.name}
                  alt=""
                  className="img-fluid rounded mb-2"
                  style={{ objectFit: "contain" }}
                />
                <Button
                  variant="outline-danger"
                  size="sm"
                  style={{ width: "100%", marginBottom: 8 }}
                  disabled={!mainPhoto?.name}
                  onClick={async () => {
                    if (!profileUser?._id || !mainPhoto?.name) return;
                    try {
                      const res = await userService.deleteUserPhoto(
                        profileUser._id,
                        mainPhoto.name,
                      );
                      const fresh = res?.user?.profileImage || null;
                      const nextPhotos = Array.isArray(fresh)
                        ? fresh
                        : (profileUser.profileImage || []).filter(
                            (p) => p?.name !== mainPhoto.name,
                          );
                      setProfileUser((prev) => ({
                        ...prev,
                        profileImage: nextPhotos,
                      }));
                      const nextIdx = Math.max(
                        0,
                        Math.min(activePhotoIdx, nextPhotos.length - 1),
                      );
                      setActivePhotoIdx(nextIdx);
                      toast.success("Фото удалено");
                    } catch (e) {
                      toast.error(
                        e?.response?.data?.message || "Ошибка удаления фото",
                      );
                    }
                  }}
                >
                  Удалить фото
                </Button>
                {photos.length > 1 && (
                  <div className="d-flex gap-2 mt-1 flex-wrap">
                    {photos.map((p, idx) => (
                      <img
                        key={p.name || idx}
                        src={API_BASE_URL + "/" + p.name}
                        alt=""
                        className={`img-thumbnail ${
                          idx === activePhotoIdx ? "border-primary" : ""
                        }`}
                        style={{
                          width: 40,
                          height: 60,
                          objectFit: "fill",
                          cursor: "pointer",
                        }}
                        onClick={() => setActivePhotoIdx(idx)}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div
                className="bg-light rounded d-flex align-items-center justify-content-center"
                style={{ height: 180 }}
              >
                Нет фото
              </div>
            )}
            <div className="d-flex gap-2 mt-2"></div>
          </Col>
          <Col md={8}>
            <h5 className="mb-2">
              {profileUser.name}{" "}
              {profileUser.isVerify && (
                <span className="text-success ms-2">✔ Верифицирован</span>
              )}
            </h5>
            <p className="mb-1">
              <strong>Логин:</strong> {profileUser.login}
            </p>
            <p className="mb-1">
              <strong>Telegram:</strong>{" "}
              {profileUser.telegramUsername || profileUser.telegramId || "—"}
            </p>
            <p className="mb-1">
              <strong>Email:</strong> {profileUser.email || "—"}
            </p>
            <p className="mb-1">
              <strong>Телефон:</strong> {profileUser.phone || "—"}
            </p>
            <p className="mb-1">
              <strong>Статус:</strong> {profileUser.status}
            </p>
            <p className="mb-1">
              <strong>Последний вход:</strong> {lastLoginText}
            </p>
            <p className="mb-1">
              <strong>Дата рождения:</strong> {profileUser.dof}
            </p>
            <p className="mb-1">
              <strong>О себе:</strong> {profileUser.profile?.about}
            </p>

            <Row className="g-2 mt-2">
              <Col xs={6}>
                <Button
                  variant={
                    profileUser.status === "blocked"
                      ? "outline-success"
                      : "outline-warning"
                  }
                  className="w-100"
                  onClick={handleBlockToggleClick}
                >
                  {profileUser.status === "blocked"
                    ? "Разблокировать"
                    : "Заблокировать"}
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  variant="outline-danger"
                  className="w-100"
                  onClick={handleDeleteClick}
                >
                  {profileUser.status === "deleted"
                    ? "Восстановить аккаунт"
                    : "Удалить аккаунт"}
                </Button>
              </Col>
            </Row>
            <Row className="g-2 mt-2">
              <Col xs={12}>
                <Button
                  variant="outline-primary"
                  className="w-100"
                  onClick={openAllChats}
                >
                  Просмотреть все чаты пользователя
                </Button>
              </Col>
            </Row>

            {showFullInfromtaion && (
              <Row className="mt-3">
                <Col xs={12}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      maxHeight: "400px",
                      overflowY: "auto",
                      fontFamily: "monospace",
                      fontSize: "12px",
                    }}
                  >
                    <pre>{JSON.stringify(profileUser, null, 2)}</pre>
                  </div>
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        <hr />

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Внутренняя заметка</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Ваши комментарии по пользователю..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={noteLoading}
              />
            </Form.Group>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveNote}
              disabled={noteLoading}
            >
              {noteLoading ? "Сохранение…" : "Сохранить заметку"}
            </Button>
          </Col>
          <Col md={6}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">История действий (последние события)</h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleDownloadHistory}
                disabled={historyLoading || history.length === 0}
              >
                Скачать историю
              </Button>
            </div>
            {historyLoading ? (
              <div className="py-2">
                <Spinner animation="border" size="sm" />
              </div>
            ) : history.length ? (
              <div
                style={{
                  maxHeight: 180,
                  overflowY: "auto",
                  border: "1px solid #eee",
                  borderRadius: 8,
                  padding: 8,
                  background: "#fafafa",
                }}
              >
                <ListGroup variant="flush" className="small">
                  {history.map((h) => (
                    <ListGroup.Item
                      key={h._id || `${h.type}-${h.createdAt}`}
                      style={{ border: "none", padding: "6px 8px" }}
                    >
                      {renderHistoryItem(h)}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            ) : (
              <div className="small text-muted">История пуста.</div>
            )}
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={12}>
            <AdminUserChat userId={profileUser._id} />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Закрыть
        </Button>
      </Modal.Footer>

      <Modal
        show={showChatsModal}
        onHide={() => setShowChatsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Чаты пользователя (только просмотр)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {chatsLoading ? (
            <div className="py-2">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <Row>
              <Col md={4}>
                <div className="small text-muted mb-2">
                  Всего чатов: {userChats.length}
                </div>
                <ListGroup>
                  {userChats.map((c) => {
                    const name = c?.participants[0]?.name;
                    const login = c?.participants[0]?.username
                      ? c?.participants[0]?.username
                      : c?.id || c?.chat_id;
                    const cid = c?.id || c?.chat_id;
                    const title =
                      c?.chatName ||
                      c?.name ||
                      `${c?.chatType === "private" ? `Чат c ${name}` : "Записи в канале" || "chat"} `;
                    return (
                      <ListGroup.Item
                        key={cid}
                        action
                        active={cid === selectedChatId}
                        onClick={() => openChatMessages(c)}
                      >
                        <div className="fw">{title}</div>
                        <div className="small text-muted">
                          chatID: {cid} • login: {login}
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                  {!userChats.length && (
                    <div className="small text-muted">Чаты не найдены.</div>
                  )}
                </ListGroup>
              </Col>
              <Col md={8}>
                {!selectedChatId ? (
                  <div className="text-muted small">
                    Выберите чат слева, чтобы увидеть сообщения.
                  </div>
                ) : messagesLoading ? (
                  <div className="py-2">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: 360,
                      overflowY: "auto",
                      border: "1px solid #eee",
                      borderRadius: 8,
                      padding: 8,
                      background: "#fafafa",
                    }}
                  >
                    <ListGroup variant="flush" className="small">
                      {selectedChatMessages.map((m) => {
                        return (
                          <ListGroup.Item
                            key={m.message_id || `${m.user_id}-${m.timestamp}`}
                            style={{ border: "none" }}
                          >
                            <div className="small text-muted mb-1">
                              {(m.User || m.user)?.username || m.user_id} •{" "}
                              {m.timestamp
                                ? new Date(m.timestamp).toLocaleString("ru-RU")
                                : ""}
                            </div>
                            <div>
                              {m.message_type === "call"
                                ? `Звонок от ${m?.User?.name}`
                                : m.content}
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                      {!selectedChatMessages.length && (
                        <div className="small text-muted">Сообщений нет.</div>
                      )}
                    </ListGroup>
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowChatsModal(false)}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default UserProfileModal;
